import { useEffect } from "react";
import { NextRouter } from "next/router";

function saveScrollPos(asPath: string) {
  sessionStorage.setItem(
    `scrollPos:${asPath}`,
    JSON.stringify({ x: window.scrollX, y: window.scrollY })
  );
}

function restoreScrollPos(asPath: string) {
  const json = sessionStorage.getItem(`scrollPos:${asPath}`);
  const scrollPos = json ? JSON.parse(json) : undefined;
  if (scrollPos) {
    window.scrollTo(scrollPos.x, scrollPos.y);
  }
}

/**
 * Based on https://gist.github.com/claus/992a5596d6532ac91b24abe24e10ae81
 *
 * next/link has scroll controls and an experimental flag called
 * `scrollRestoration` but it does not work when using `getServerSideProps`
 * as reported in https://github.com/vercel/next.js/issues/12530 and
 * https://github.com/vercel/next.js/issues/3303
 *
 * We don't really want to use the scroll controls anyway, because for our use
 * case we don't want to restore scroll everywhere. This allows us to specify
 * exactly which pages we want to save and restore scroll.
 */

/**
 * Saves the scroll position upon navigation in session storage.
 */
export const useSaveScroll = (router: NextRouter) => {
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;
    window.history.scrollRestoration = "manual";

    const onRouteChangeStart = () => {
      saveScrollPos(router.asPath);
    };

    router.events.on("routeChangeStart", onRouteChangeStart);

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
    };
  }, [router]);
};

/**
 * Restores the scroll position upon clicking the back button,
 * if a scroll position exists for the previous page in session storage.
 */
export const useRestoreScrollOnBrowserBack = (router: NextRouter) => {
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;
    let shouldScrollRestore = false;
    window.history.scrollRestoration = "manual";

    const onRouteChangeComplete = (url: string) => {
      if (shouldScrollRestore) {
        shouldScrollRestore = false;
        /**
         * Calling with relative url, not expected asPath, so this
         * will break if there is a basePath or locale path prefix.
         */
        restoreScrollPos(url);
      }
    };

    router.events.on("routeChangeComplete", onRouteChangeComplete);
    router.beforePopState(() => {
      shouldScrollRestore = true;
      return true;
    });

    return () => {
      router.events.off("routeChangeComplete", onRouteChangeComplete);
      router.beforePopState(() => true);
    };
  }, [router]);
};
